import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const avatarUrl = (userId, v) =>
  userId && v ? `${API}/users/${userId}/avatar?v=${encodeURIComponent(v)}` : null;

// Global timeout - 15 seconds
axios.defaults.timeout = 15000;

/**
 * Sanitize error responses so we never leak HTML/Cloudflare gibberish to the UI.
 * Only trusts a `detail` field that is short, plain text and not HTML.
 * Falls back to a friendly message for network/gateway errors.
 */
export const getErrorMessage = (err, fallback = 'Ops, algo deu errado. Tente novamente.') => {
  const data = err?.response?.data;
  const status = err?.response?.status;
  const looksLikeHtml = (s) => typeof s === 'string' && (/<\s*(html|body|head|!doctype)/i.test(s) || /&lt;|&gt;/.test(s));

  // Network / gateway errors — never show provider HTML
  if (!err?.response || err?.code === 'ECONNABORTED' || err?.code === 'ERR_NETWORK') {
    return 'Sem conexão com o servidor. Verifique sua internet e tente novamente.';
  }
  if (status && status >= 502 && status <= 524) {
    return 'O servidor está temporariamente indisponível. Tente novamente em alguns segundos.';
  }

  // FastAPI style {detail: "..."} — accept only clean short strings
  if (data && typeof data === 'object' && typeof data.detail === 'string') {
    const d = data.detail.trim();
    if (d && d.length < 300 && !looksLikeHtml(d)) return d;
  }

  // Sometimes servers return plain text — reject HTML/CF blobs
  if (typeof data === 'string' && data.length < 300 && !looksLikeHtml(data)) {
    return data;
  }

  return fallback;
};

// Callback for forcing logout when token is invalid
let onAuthError = null;
export const setOnAuthError = (callback) => {
  onAuthError = callback;
};

// Retry logic for network errors and timeouts (GET requests only)
axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const config = error.config;
    const status = error?.response?.status;

    // Retry: GET requests on network failures/timeouts (up to 2 retries)
    // OR any request on 502/503/504 (transient gateway issues) — 1 retry
    if (config && !config._retryCount) {
      const isNetwork = !error.response || error.code === 'ECONNABORTED' || error.code === 'ERR_NETWORK';
      const isTransientGateway = status === 502 || status === 503 || status === 504;

      if ((config.method === 'get' && isNetwork) || isTransientGateway) {
        config._retryCount = (config._retryCount || 0) + 1;
        const max = isTransientGateway ? 1 : 2;
        if (config._retryCount <= max) {
          await new Promise((r) => setTimeout(r, config._retryCount * 1000));
          return axios(config);
        }
      }
    }

    // Handle 401
    if (error.response?.status === 401) {
      const url = config?.url || '';
      if (!url.includes('/auth/login') && !url.includes('/auth/register')) {
        console.warn('[Auth] Token expired or invalid — forcing re-login');
        clearAuth();
        if (onAuthError) onAuthError();
      }
    }
    return Promise.reject(error);
  }
);

export const resolvePhotoUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('/api/')) return `${BACKEND_URL}${url}`;
  return url;
};

export const setAuthToken = (token) => {
  if (token) {
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    localStorage.setItem('token', token);
  } else {
    delete axios.defaults.headers.common['Authorization'];
    localStorage.removeItem('token');
  }
};

export const getStoredToken = () => {
  return localStorage.getItem('token');
};

export const getStoredUser = () => {
  const user = localStorage.getItem('user');
  try {
    return user ? JSON.parse(user) : null;
  } catch {
    return null;
  }
};

export const setStoredUser = (user) => {
  if (user) {
    localStorage.setItem('user', JSON.stringify(user));
  } else {
    localStorage.removeItem('user');
  }
};

export const clearAuth = () => {
  delete axios.defaults.headers.common['Authorization'];
  localStorage.removeItem('token');
  localStorage.removeItem('user');
};

// Validate the stored token with the backend, returns fresh user or null
export const validateSession = async () => {
  const token = getStoredToken();
  if (!token) return null;
  try {
    setAuthToken(token);
    const res = await axios.get(`${API}/users/me`);
    const freshUser = res.data;
    setStoredUser(freshUser);
    return freshUser;
  } catch (error) {
    // Only clear auth on 401 (invalid token), NOT on network errors
    if (error.response?.status === 401) {
      clearAuth();
      return null;
    }
    // Network error / timeout — keep user logged in with cached data
    return getStoredUser();
  }
};

// Restore token to axios on module load
if (getStoredToken()) {
  setAuthToken(getStoredToken());
}
